import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Squares2X2Icon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ChevronUpDownIcon,
  ArrowRightOnRectangleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import {
  Squares2X2Icon as Squares2X2IconSolid,
  UserGroupIcon as UserGroupIconSolid,
  BuildingOfficeIcon as BuildingOfficeIconSolid,
  CalendarDaysIcon as CalendarDaysIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
} from '@heroicons/react/24/solid';

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange, mobileOpen, onMobileClose }) => {
  const { adminUser, logout } = useAuth();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });

  // Persist collapse state to localStorage
  useEffect(() => {
    const savedCollapsed = localStorage.getItem('adminSidebarCollapsed');
    if (savedCollapsed !== null) {
      setIsCollapsed(savedCollapsed === 'true');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', isCollapsed.toString());
    window.dispatchEvent(new Event('sidebarToggle'));
  }, [isCollapsed]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  // Overview navigation items
  const overviewNavigation = [
    { id: 'dashboard', name: 'Dashboard', icon: Squares2X2Icon, iconActive: Squares2X2IconSolid },
    { id: 'organizations', name: 'Organizations', icon: BuildingOfficeIcon, iconActive: BuildingOfficeIconSolid },
    { id: 'users', name: 'Users', icon: UserGroupIcon, iconActive: UserGroupIconSolid },
    { id: 'sessions', name: 'Sessions', icon: CalendarDaysIcon, iconActive: CalendarDaysIconSolid }
  ];

  // Settings navigation items
  const settingsNavigation = [
    { id: 'settings', name: 'Settings', icon: Cog6ToothIcon, iconActive: Cog6ToothIconSolid }
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Render a navigation item
  const renderNavItem = (item: typeof overviewNavigation[0], forMobile = false) => {
    const Icon = activeTab === item.id ? item.iconActive : item.icon;
    const isActive = activeTab === item.id;
    const collapsed = forMobile ? false : isCollapsed;

    return (
      <div
        key={item.id}
        className="relative group"
        onMouseEnter={(e) => {
          if (collapsed) {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPosition({ top: rect.top + rect.height / 2 });
            setHoveredItem(item.name);
          }
        }}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <button
          onClick={() => {
            onTabChange(item.id);
            if (forMobile) onMobileClose?.();
          }}
          {...(isActive ? { 'aria-current': 'page' as const } : {})}
          className={`
            w-full flex items-center rounded-2xl cursor-pointer
            transition-all duration-300 ease-out
            relative overflow-hidden
            ${collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3.5 py-2.5'}
            ${isActive
              ? 'bg-gray-800/50 backdrop-blur-sm text-white'
              : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 hover:backdrop-blur-sm'
            }
            group-hover:scale-[1.02] active:scale-[0.98]
          `}
        >

          <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-all duration-300 ${isActive ? 'text-white scale-110' : 'group-hover:scale-105'}`} />
          {!collapsed && (
            <span className={`text-[13px] transition-all duration-300 ${isActive ? 'font-semibold' : 'font-normal'}`}>
              {item.name}
            </span>
          )}
        </button>

        {/* Tooltip for collapsed state - More rounded, no shadow */}
        {collapsed && hoveredItem === item.name && (
          <div
            className="fixed left-[64px] px-3 py-1.5
              bg-gradient-to-r from-sidebar-tooltip to-[#1f2233] text-white text-xs font-medium rounded-xl
              whitespace-nowrap z-50
              border border-gray-600/30 pointer-events-none
              animate-fadeIn backdrop-blur-sm"
            style={{ top: tooltipPosition.top, transform: 'translateY(-50%)' }}
          >
            {item.name}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-[7px] border-transparent border-r-sidebar-tooltip" />
          </div>
        )}
      </div>
    );
  };

  // Render navigation sections (shared between desktop and mobile)
  const renderNavigationContent = (forMobile = false) => {
    const collapsed = forMobile ? false : isCollapsed;

    return (
      <>
        {/* Overview Section */}
        <div>
          {!collapsed && (
            <div className="px-3.5 pb-2 mb-0.5">
              <span className="text-[10px] font-semibold text-gray-500/70 uppercase tracking-widest">
                Overview
              </span>
            </div>
          )}
          <nav className={`${collapsed ? 'space-y-1 pt-2' : 'space-y-0.5'}`}>
            {overviewNavigation.map((item) => renderNavItem(item, forMobile))}
          </nav>
        </div>

        {/* Settings Section */}
        <div className={`border-t border-sidebar-border/50 dark:border-gray-800/50 ${collapsed ? 'mt-3' : 'mt-4'}`}>
          {!collapsed && (
            <div className="px-3.5 pb-2 pt-4 mb-0.5">
              <span className="text-[10px] font-semibold text-gray-500/70 uppercase tracking-widest">
                Settings
              </span>
            </div>
          )}
          <nav className={`${collapsed ? 'space-y-1 pt-3' : 'space-y-0.5'}`}>
            {settingsNavigation.map((item) => renderNavItem(item, forMobile))}
          </nav>
        </div>
      </>
    );
  };

  // Render user profile section (shared between desktop and mobile)
  const renderUserProfile = (forMobile = false) => {
    const collapsed = forMobile ? false : isCollapsed;

    return (
      <div className={`${collapsed ? 'p-2' : 'p-3'}`}>
        <div className="relative">
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            aria-expanded={showUserDropdown}
            aria-haspopup="menu"
            className={`
              w-full flex items-center rounded-xl transition-all duration-300 ease-out
              border border-gray-700/30 hover:border-gray-600/50
              bg-gradient-to-br from-[#1a1d2e]/20 to-transparent
              hover:from-[#1a1d2e]/40 hover:to-[#1a1d2e]/10
              ${collapsed ? 'justify-center p-2' : 'gap-2.5 p-2.5'}
              hover:scale-[1.02] active:scale-[0.98]
            `}
          >
            <div className="relative flex-shrink-0">
              <img
                src={adminUser?.photoURL || `https://ui-avatars.com/api/?name=${adminUser?.displayName}&background=3b82f6&color=fff`}
                alt={adminUser?.displayName || 'Admin'}
                className={`rounded-full ring-2 ring-gray-700/50 ${collapsed ? 'w-8 h-8' : 'w-9 h-9'} transition-all duration-300`}
              />
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-400 border-2 border-sidebar-bg rounded-full" />
            </div>

            {!collapsed && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-semibold text-white truncate">
                    {adminUser?.displayName || 'Admin'}
                  </p>
                  <p className="text-[11px] text-gray-400 truncate">
                    {adminUser?.email}
                  </p>
                </div>
                <ChevronUpDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0 transition-transform duration-300" />
              </>
            )}
          </button>

          {/* User Dropdown */}
          {showUserDropdown && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserDropdown(false)}
              />
              <div className={`absolute ${collapsed ? 'left-full ml-2 bottom-0' : 'bottom-full left-0 right-0 mb-3'}
                bg-gray-800/50 backdrop-blur-sm
                rounded-2xl border border-transparent
                z-50 animate-fadeIn overflow-hidden`}>
                <div className="py-1.5">
                  {/* Current Role Indicator */}
                  <div className="px-5 py-3 text-xs text-gray-400 border-b border-gray-600/20">
                    Current Role: <span className="text-white font-semibold">SuperAdmin</span>
                  </div>

                  <button
                    onClick={() => {
                      handleLogout();
                      setShowUserDropdown(false);
                    }}
                    className="w-full text-left px-5 py-3 text-red-400 text-sm
                      hover:bg-white/5 transition-all duration-300
                      flex items-center gap-3 rounded-b-2xl
                      hover:text-red-300"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`hidden lg:flex h-screen flex-col fixed left-0 top-0 z-40 transition-all duration-300
          bg-sidebar-bg dark:bg-gray-900 border-r border-sidebar-border dark:border-transparent ${isCollapsed ? 'w-[60px]' : 'w-60'}`}
      >
        {/* Logo Section — collapsed shows the icon; expanded shows the full
            combined logo (icon + wordmark). Don't render both, or the "V" mark
            appears twice. */}
        <div className={`flex items-center transition-all duration-300 ${isCollapsed ? 'justify-center px-2 pt-5 pb-4' : 'px-5 pt-5 pb-4'}`}>
          {isCollapsed ? (
            <img
              src="/icon-virtualy.png"
              alt="Virtual Client"
              className="object-contain h-7 w-7 transition-all duration-300"
            />
          ) : (
            <img
              src="/v1-light.png"
              alt="Virtual Client"
              className="object-contain h-7 w-auto"
            />
          )}
        </div>

        {/* Divider */}
        <div className="border-b border-sidebar-border/50 dark:border-gray-800/50 mb-2"></div>

        {/* Navigation Content */}
        <div className={`flex-grow flex flex-col overflow-y-auto sidebar-scrollbar transition-all duration-300 ${isCollapsed ? 'px-2 pt-3 pb-3' : 'px-3 py-3'}`}>
          {renderNavigationContent(false)}

          {/* Spacer */}
          <div className="flex-grow" />

          {/* Collapse Toggle Button on Divider */}
          <div className={`relative border-t border-sidebar-border/50 dark:border-gray-800/50 ${isCollapsed ? 'mt-5 mb-5' : 'mt-6 mb-6'}`}>
            <button
              onClick={toggleCollapse}
              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2
                p-1.5 rounded-full border transition-all duration-300 cursor-pointer
                bg-sidebar-bg dark:bg-gray-900
                border-sidebar-border/50 dark:border-gray-800/50
                text-gray-500 hover:text-white
                hover:border-gray-600/50
                hover:bg-sidebar-hover
                hover:scale-110 active:scale-95"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <ChevronDoubleRightIcon className="w-4 h-4 transition-transform duration-300" />
              ) : (
                <ChevronDoubleLeftIcon className="w-4 h-4 transition-transform duration-300" />
              )}
            </button>
          </div>
        </div>

        {/* Bottom Section - User Profile */}
        {renderUserProfile(false)}
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onMobileClose} />
          <div className="fixed inset-y-0 left-0 w-72 z-50 lg:hidden bg-sidebar-bg flex flex-col h-screen animate-slideInLeft">
            {/* Mobile Header: Logo + Close Button */}
            <div className="flex items-center justify-between px-6 pt-8 pb-6">
              <div className="flex items-center gap-3">
                <img
                  src="/v1-light.png"
                  alt="Virtual Client"
                  className="object-contain h-8 w-auto"
                />
              </div>
              <button
                onClick={onMobileClose}
                className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800/50 transition-colors"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Divider */}
            <div className="border-b border-sidebar-border/50 dark:border-gray-800/50 mb-4"></div>

            {/* Navigation Content */}
            <div className="flex-grow flex flex-col overflow-y-auto sidebar-scrollbar px-4 py-4">
              {renderNavigationContent(true)}

              {/* Spacer */}
              <div className="flex-grow" />
            </div>

            {/* Bottom Section - User Profile */}
            {renderUserProfile(true)}
          </div>
        </>
      )}
    </>
  );
};

export default Sidebar;
